import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Flame, Send, ListChecks, Inbox, Wallet, Shield, LogOut, Rss, Settings as SettingsIcon, Zap, Swords, Trophy, ChevronDown } from "lucide-react";
import NotificationBell from "./NotificationBell";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "./ui/dropdown-menu";

// Brand-mode / funnel items lead the nav (what a visitor decides between).
const primaryNav = [
  { to: "/app/submit", label: "Roast", icon: Send, tid: "nav-submit" },
  { to: "/app/battles", label: "Roast-Off", icon: Swords, tid: "nav-battles" },
  { to: "/app/arena", label: "Arena", icon: Trophy, tid: "nav-arena" },
  { to: "/hot-takes", label: "Hot Takes", icon: Zap, tid: "nav-hottakes" },
  { to: "/app/feed", label: "Feed", icon: Rss, tid: "nav-feed" },
];
// Utility screens for active users — tucked under "More".
const moreNav = [
  { to: "/app/queue", label: "Queue", icon: ListChecks, tid: "nav-queue" },
  { to: "/app/inbox", label: "Inbox", icon: Inbox, tid: "nav-inbox" },
  { to: "/app/earnings", label: "Earnings", icon: Wallet, tid: "nav-earnings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const moreActive = moreNav.some((n) => location.pathname === n.to);

  return (
    <div className="min-h-screen grain">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="brand-logo" onClick={() => navigate("/app/submit")} className="flex items-center gap-2 group shrink-0">
            <Flame className="text-brand group-hover:scale-110 transition-transform" size={26} />
            <span className="font-heading text-2xl tracking-tight">RoastLit</span>
          </button>
          <nav className="hidden md:flex items-center gap-1">
            {primaryNav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.tid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "bg-brand text-black" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                <n.icon size={16} /> {n.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-more" className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors outline-none ${moreActive ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}>
                  More <ChevronDown size={14} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-950 border-white/10 text-zinc-200">
                {moreNav.map((n) => (
                  <DropdownMenuItem key={n.to} data-testid={`${n.tid}-more`} onClick={() => navigate(n.to)}
                    className="gap-2 cursor-pointer focus:bg-white/10 focus:text-white">
                    <n.icon size={15} /> {n.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
          <div className="flex items-center gap-3 shrink-0">
            {user && <NotificationBell />}
            {user?.is_admin && (
              <NavLink to="/app/admin" data-testid="nav-admin" title="Admin"
                className={({ isActive }) => `transition-colors ${isActive ? "text-gold" : "text-zinc-400 hover:text-gold"}`}>
                <Shield size={18} />
              </NavLink>
            )}
            <NavLink to="/app/settings" data-testid="nav-settings" title="Settings" className={({ isActive }) => `transition-colors ${isActive ? "text-brand" : "text-zinc-400 hover:text-brand"}`}><SettingsIcon size={18} /></NavLink>
            <span className="hidden lg:block text-xs text-zinc-400">{user?.name}</span>
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/20" />}
            <button data-testid="logout-btn" onClick={logout} className="text-zinc-400 hover:text-brand transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
        <nav className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto">
          {[...primaryNav, ...moreNav].map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`${n.tid}-mobile`}
              className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${isActive ? "bg-brand text-black" : "text-zinc-400 bg-white/5"}`}>
              <n.icon size={14} /> {n.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink to="/app/admin" className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${isActive ? "bg-gold text-black" : "text-zinc-400 bg-white/5"}`}>
              <Shield size={14} /> Admin
            </NavLink>
          )}
        </nav>
      </header>
      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-8">{children}</main>
    </div>
  );
}
